import React from 'react';

import type { AiAction } from '~/components/AiChat/types';
import type { ActionCardModel } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useActionCard';
import ActionGestures from '~/components/ClaimPage/CliveApp/ConductorHub/ActionGestures';
import { ACTION_NEEDS_ATTENTION } from '~/components/ClaimPage/CliveApp/ConductorHub/constants';
import PayloadList from '~/components/ClaimPage/CliveApp/ConductorHub/PayloadList';
import RejectionReason from '~/components/ClaimPage/CliveApp/ConductorHub/RejectionReason';
import SettledActionPreview, {
  isPreviewableOnceSettled,
} from '~/components/ClaimPage/CliveApp/ConductorHub/SettledActionPreview';
import { TEXTS } from '~/components/ClaimPage/CliveApp/ConductorHub/texts';
import type { ActionState } from '~/components/ClaimPage/CliveApp/ConductorHub/types';
import {
  actionReasonLabel,
  actionRejectionReason,
  actionWaitingReason,
} from '~/components/ClaimPage/CliveApp/ConductorHub/utils';
import Text from '~/components/core/TextComponents/Text';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import ExplanationToggle from './ExplanationToggle';

interface ActionDetailProps {
  action: AiAction;
  state: ActionState;
  /** The entry's `useActionCard`: the explanation's renderer and the gestures. */
  model: ActionCardModel;
}

/**
 * The body of a log card: when it was decided, why Clive proposed it, what it writes to the claim
 * and the note it was rejected with — and, while it is waiting, why it needs approval where CMS said,
 * and Execute and Reject.
 */
const ActionDetail: React.FC<ActionDetailProps> = ({ action, state, model }) => {
  const { InnerComponent } = model;
  const isWaiting = ACTION_NEEDS_ATTENTION[state];
  const decidedAt = action.status_change?.at;

  return (
    <div className="flex flex-col gap-12">
      {decidedAt ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {`Decided ${serverDateTimeToLocal(decidedAt)}`}
        </Text>
      ) : null}

      {/* The conductor writes an empty explanation where it had none; a toggle over nothing is noise. */}
      {InnerComponent && action.task_data?.explanation ? (
        <ExplanationToggle
          key={isWaiting ? 'waiting' : 'settled'}
          action={action}
          InnerComponent={InnerComponent}
          isOpenByDefault={isWaiting}
        />
      ) : null}

      <PayloadList action={action} state={state} />

      <RejectionReason reason={actionWaitingReason(state, action)?.text ?? null} label={TEXTS.WAITING_REASON} />

      <RejectionReason reason={actionRejectionReason(state, action)} label={actionReasonLabel(state)} />

      {isWaiting ? (
        <ActionGestures action={action} model={model} />
      ) : isPreviewableOnceSettled(action) ? (
        <SettledActionPreview action={action} subtitle={model.relatedExposureLabel} />
      ) : null}
    </div>
  );
};

export default ActionDetail;
